
























import { Component, Prop } from 'vue-property-decorator'
import { Chart } from 'highcharts-vue'
import Highcharts from 'highcharts'
import * as gql from '@shared/types/graphql'
import OpenEndsCard from '@/components/Survey/TextResponses/OpenEndsCard.vue'
import { BaseQuestion } from '../BaseQuestion'
import Tabs from '@/components/Tabs/Tabs.vue'
import Tab from '@/components/Tabs/Tab.vue'
import {
  calcPercentage,
  generateIndexSeries,
  generateSentimentSubtitle,
  generateSlicedSeries,
  getCategoriesForSlicedQuestion,
  getCategoriesForSlicedVariantQuestion,
  getResponseSentimentTotals,
  getSegmentCount,
  getSentimentTotalsBySegment,
  Series,
  SeriesData,
  TextAreaChartType
} from '@/components/Survey/Utilities/ChartUtilities'
import { capitalize } from 'lodash-es'
import { TabData } from '@/components/Tabs/TabTypes'
import SliceNotSupported from '@/components/Survey/SliceNotSupported.vue'
import { generateDivergingColors } from '../../Utilities/ColorUtility'

@Component({
  components: {
    Highcharts: Chart,
    Tabs,
    Tab,
    OpenEndsCard,
    SliceNotSupported
  }
})
export default class Sentiment extends BaseQuestion {
  @Prop({ required: true }) chartType!: TextAreaChartType
  highcharts = Highcharts
  selectedPhrase = ''

  get maxDimensions () {
    return this.isSliced && this.isVariant
  }

  get chartHasData () {
    if (this.isSliced && this.isVariant) {
      return getSegmentCount(this.typedFilteredSlicedVariantResponses[this.selectedTab]) > 0
    } else if (this.isSliced) {
      return getSegmentCount(this.typedFilteredSlicedResponses) > 0
    }
    return this.filteredIndexResponses.length > 0
  }

  get indexTotals () {
    return getResponseSentimentTotals(this.filteredIndexResponses)
  }

  get segmentTotals () {
    if (this.isSliced && this.isVariant) {
      return getSentimentTotalsBySegment(this.typedFilteredSlicedVariantResponses[this.selectedTab])
    } else {
      return getSentimentTotalsBySegment(this.typedFilteredSlicedResponses)
    }
  }

  get series () {
    if (this.isSliced || this.isVariant) {
      return this.slicedSeries()
    } else {
      return generateIndexSeries(this.indexSeriesData)
    }
  }

  slicedSeries () {
    const slicedSeriesData = generateSlicedSeries(this.segmentTotals, this.colors, this.sortedCategories)
    const sortedData: Series[] = []
    this.sortedCategories.forEach(category => {
      const series = slicedSeriesData.find(o => o.name === category)
      if (series) {
        sortedData.push(series)
      }
    })

    return sortedData
  }

  get indexSeriesData () : SeriesData[] {
    return this.questionOptions.map((category, index) => {
      return {
        name: capitalize(category.text),
        y: calcPercentage(this.indexTotals[category.text], this.filteredIndexResponses.length),
        color: this.colors[index]
      }
    })
  }

  get colors () {
    return generateDivergingColors(3, this.colorConfiguration.divergentPalette)
  }

  get chartOptions () {
    return {
      ...this.defaultChartOptions(this.displayCategories),
      series: this.series,
      xAxis: {
        ...this.defaultChartOptions(this.displayCategories).xAxis,
        categories: this.displayCategories
      },
      exporting: {
        ...this.defaultChartOptions(this.displayCategories).exporting,
        chartOptions: {
          ...this.defaultChartOptions(this.displayCategories).exporting.chartOptions,
          subtitle: {
            text: this.chartSubtitle
          }
        }
      }
    }
  }

  get displayCategories () {
    if (this.isSliced && this.isVariant) {
      return getCategoriesForSlicedVariantQuestion(this.typedFilteredSlicedVariantResponses, this.selectedTab)
    } else if (this.isSliced || this.isVariant) {
      return getCategoriesForSlicedQuestion(this.segmentTotals, this.sliceByQuestion)
    } else {
      return this.sortedCategories
    }
  }

  get chartSubtitle () {
    const nSize = this.isSliced && this.isVariant
      ? getSegmentCount(this.typedFilteredSlicedVariantResponses[this.selectedTab])
      : this.filteredIndexResponses.length

    const variantTab = this.isSliced && this.isVariant ? this.selectedTab : ''

    return generateSentimentSubtitle(nSize, this.sliceBy, variantTab)
  }

  get sortedCategories () {
    return this.indexSeriesData.map(data => data.name!)
  }

  get questionOptions () {
    return [
      {
        label: capitalize(gql.SentimentLabel.Positive),
        text: capitalize(gql.SentimentLabel.Positive),
        shuffle: false,
        order: 0
      },
      {
        label: capitalize(gql.SentimentLabel.Neutral),
        text: capitalize(gql.SentimentLabel.Neutral),
        shuffle: false,
        order: 1
      },
      {
        label: capitalize(gql.SentimentLabel.Negative),
        text: capitalize(gql.SentimentLabel.Negative),
        shuffle: false,
        order: 2
      }
    ]
  }

  get tabData (): TabData[] {
    return Object.entries(this.typedFilteredSlicedResponses).map(([key, responses]) => {
      return {
        title: key,
        segmentCount: getSegmentCount(responses)
      }
    })
  }
}
